/**
 * Generates a markdown representation of the current page state
 * for the AI assistant. Inspired by Cloudflare's "Markdown for Agents"
 * but generated directly from React state (more precise, fewer tokens).
 */
export function generatePageContextMarkdown() {
  const path = window.location.pathname;
  const ctx = window.__PAGE_CONTEXT__;

  if (!ctx) {
    return `# Current Page\nPath: ${path}\nNo page context available.`;
  }

  const sections = [];

  // Page header
  sections.push(`# ${ctx.pageTitle || 'Unknown Page'}`);
  sections.push(`Path: ${path}`);

  // Active filters
  const filters = ctx.activeFilters || {};
  const activeFilterKeys = Object.keys(filters).filter(k => filters[k] != null && filters[k] !== '' && filters[k] !== undefined);
  if (activeFilterKeys.length > 0) {
    sections.push('\n## Active Filters');
    activeFilterKeys.forEach(key => {
      const val = filters[key];
      const display = Array.isArray(val) ? val.join(', ') : String(val);
      sections.push(`- ${key}: ${display}`);
    });
  }

  // Table data
  const records = ctx.visibleRecords || [];
  if (records.length > 0) {
    const total = ctx.totalCount || records.length;
    const page = ctx.currentPage || 1;
    sections.push(`\n## Visible Data (${total} total, page ${page})`);

    const columns = ctx.visibleColumns || Object.keys(records[0] || {}).filter(k => k !== 'id' && !k.startsWith('_'));
    const displayRecords = records.slice(0, 20);

    // Markdown table header
    sections.push('| # | ' + columns.join(' | ') + ' |');
    sections.push('|---|' + columns.map(() => '---').join('|') + '|');

    // Rows
    displayRecords.forEach((record, i) => {
      const cells = columns.map(col => {
        const val = record[col];
        if (val == null) return '-';
        const str = String(val);
        return str.length > 25 ? str.substring(0, 22) + '...' : str;
      });
      sections.push(`| ${i + 1} | ${cells.join(' | ')} |`);
    });

    if (total > 20) {
      sections.push(`\n> ${total - 20} more records not shown`);
    }
  }

  // Selected records
  const selected = ctx.selectedRecords || [];
  if (selected.length > 0) {
    sections.push('\n## Selected Records');
    selected.forEach(r => {
      sections.push(`- ${r.displayName || r.id} (ID: ${r.id})`);
    });
  }

  // Available actions
  const actions = ctx.availableActions || [];
  if (actions.length > 0) {
    sections.push('\n## Available Actions');
    actions.forEach(a => sections.push(`- ${a}`));
  }

  return sections.join('\n');
}

/**
 * Helper to set page context from a list page component.
 * Call in useEffect when data changes.
 *
 * @param {Object} config
 * @param {string} config.pageTitle - Display name of the page
 * @param {Object} config.activeFilters - Current filter key-value pairs
 * @param {Array} config.visibleRecords - Array of record objects currently displayed
 * @param {Array} config.visibleColumns - Column keys to include in markdown
 * @param {number} config.totalCount - Total records matching filters
 * @param {number} config.currentPage - Current pagination page
 * @param {Array} config.selectedRecords - Currently selected records [{id, displayName}]
 * @param {Array} config.availableActions - Available operations on this page
 */
export function setPageContext(config) {
  window.__PAGE_CONTEXT__ = config;
}

/**
 * Clear page context. Call on component unmount.
 */
export function clearPageContext() {
  window.__PAGE_CONTEXT__ = null;
}
